/**
 * CollapsibleSection - Expandable section for nested arrays in entity change cards.
 *
 * Shows title + count. Default expansion follows a standard rule:
 *   - CREATED / DELETED → expanded (show everything)
 *   - UPDATED           → expanded only when hasChanges
 *   - SAME              → collapsed
 *
 * Pass `changeType` so the section computes its own default.
 * `defaultExpanded` is available as an explicit override when needed.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import type { ChangeType } from '@/services/api/generated/crossMatterChangesService';

export const CollapsibleSection: React.FC<{
  title: string;
  count: number;
  changeType?: ChangeType;
  defaultExpanded?: boolean;
  hasChanges?: boolean;
  children: React.ReactNode;
}> = ({
  title,
  count,
  changeType,
  defaultExpanded,
  hasChanges = false,
  children,
}) => {
  const computedDefault =
    defaultExpanded ??
    (changeType === 'CREATED' || changeType === 'DELETED' || hasChanges);

  const [expanded, setExpanded] = useState(computedDefault);

  if (count === 0 && !hasChanges) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors w-full text-left py-1"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        <span>{title}</span>
        <span className="text-gray-400">({count})</span>
        {hasChanges && (
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        )}
      </button>
      {expanded && <div className="mt-2 space-y-2 pl-5">{children}</div>}
    </div>
  );
};
